import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { Invoice, Quotation } from '@/types/invoice';
import { format } from 'date-fns';

/**
 * Captures the invoice element as a full-A4-height canvas for PDF export.
 *
 * The element's background (colour/gradient from the selected template) is
 * expanded to fill the full A4 page height so there is no blank white gap at
 * the bottom. No SVG decoration manipulation is needed — templates are
 * colour-only.
 *
 * All style mutations are fully restored after the canvas is produced.
 */
async function captureElement(element: HTMLElement): Promise<HTMLCanvasElement> {
  // At 192 DPI (= 96-dpi base × scale:2), A4 = 1587 × 2245 px.
  // Divide by the scale factor to get the pre-scale base dimensions:
  //   width  = Math.round(1587 / 2) = 794 px
  //   height = Math.round(2245 / 2) = 1123 px  (rounds up from 1122.5)
  const SCALE = 2;
  const A4_W  = Math.round(1587 / SCALE); // 794 px
  const A4_H  = Math.round(2245 / SCALE); // 1123 px

  // Snapshot styles we will mutate
  const saved = {
    overflow:     element.style.overflow,
    borderRadius: element.style.borderRadius,
    boxShadow:    element.style.boxShadow,
    width:        element.style.width,
    minWidth:     element.style.minWidth,
    maxWidth:     element.style.maxWidth,
    height:       element.style.height,
    minHeight:    element.style.minHeight,
  };

  // 1. Force the width of the element to exactly A4 width (794px).
  // This standardizes the line wrapping and grid layout so it fits perfectly on A4 paper width.
  element.style.width     = `${A4_W}px`;
  element.style.minWidth  = `${A4_W}px`;
  element.style.maxWidth  = `${A4_W}px`;

  // Force layout calculation
  element.offsetHeight;

  // Detect and prevent elements from breaking across page boundaries
  const targets = Array.from(element.querySelectorAll('tbody tr, [data-page-break-avoid="true"]')) as HTMLElement[];
  const createdSpacers: HTMLElement[] = [];

  for (let i = 0; i < targets.length; i++) {
    const target = targets[i];
    const rect = target.getBoundingClientRect();
    const parentRect = element.getBoundingClientRect();
    
    const targetTop = rect.top - parentRect.top;
    const targetBottom = rect.bottom - parentRect.top;
    
    const pageIndex = Math.floor(targetTop / A4_H);
    const boundary = (pageIndex + 1) * A4_H;
    
    // If the element crosses a page boundary, push it to the next page
    if (targetTop < boundary && targetBottom > boundary) {
      const pushDown = boundary - targetTop;
      if (pushDown > 0) {
        if (target.tagName.toLowerCase() === 'tr') {
          const spacerRow = document.createElement('tr');
          const spacerCell = document.createElement('td');
          spacerCell.colSpan = 100;
          spacerCell.style.height = `${pushDown}px`;
          spacerCell.style.border = 'none';
          spacerCell.style.padding = '0';
          spacerCell.style.margin = '0';
          spacerRow.appendChild(spacerCell);
          
          target.parentNode?.insertBefore(spacerRow, target);
          createdSpacers.push(spacerRow);
        } else {
          const spacerDiv = document.createElement('div');
          spacerDiv.style.height = `${pushDown}px`;
          spacerDiv.style.clear = 'both';
          spacerDiv.style.border = 'none';
          spacerDiv.style.padding = '0';
          spacerDiv.style.margin = '0';
          
          target.parentNode?.insertBefore(spacerDiv, target);
          createdSpacers.push(spacerDiv);
        }
      }
    }
  }

  // 2. Measure the content height at this forced width with spacers.
  const contentHeight = element.scrollHeight;

  // 3. Round the height to the nearest higher multiple of A4 page height (1123px).
  // This guarantees that the background gradient fits exactly N A4 pages without leaving a white gap at the bottom of the final page.
  const numPages = Math.ceil(contentHeight / A4_H);
  const h = numPages * A4_H;

  element.style.overflow     = 'visible';
  element.style.borderRadius = '0';
  element.style.boxShadow    = 'none';
  element.style.height       = `${h}px`;
  element.style.minHeight    = `${h}px`;

  // Allow the browser to repaint with the new dimensions
  await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));

  const canvas = await html2canvas(element, {
    scale:  SCALE,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: null,   // preserve the template's own background gradient
    foreignObjectRendering: false,
    imageTimeout: 15000,
    width:  A4_W,            // capture exactly A4 width
    height: h,               // capture the calculated page-multiple height
  });

  // Remove all created spacers
  createdSpacers.forEach(spacer => spacer.parentNode?.removeChild(spacer));

  // Restore all mutated styles
  element.style.overflow     = saved.overflow;
  element.style.borderRadius = saved.borderRadius;
  element.style.boxShadow    = saved.boxShadow;
  element.style.width        = saved.width;
  element.style.minWidth     = saved.minWidth;
  element.style.maxWidth     = saved.maxWidth;
  element.style.height       = saved.height;
  element.style.minHeight    = saved.minHeight;

  return canvas;
}


/**
 * Export invoice as PDF
 */
export async function exportInvoiceToPDF(invoice: Invoice, elementId?: string): Promise<void> {
  try {
    // If an element ID is provided, capture it directly
    if (elementId) {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error('Invoice element not found');
      }

      console.log('Capturing invoice with template design...');
      const canvas = await captureElement(element);
      console.log(`Canvas created: ${canvas.width}x${canvas.height}`);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidthMm  = 210; // A4 width in mm
      const pageHeightMm = 297; // A4 height in mm
      const imgHeight    = (canvas.height * pageWidthMm) / canvas.width;
      let heightLeft     = imgHeight;
      let position       = 0;

      // First page — image fills edge to edge so the template background shows fully
      pdf.addImage(imgData, 'PNG', 0, position, pageWidthMm, imgHeight);
      heightLeft -= pageHeightMm;

      // Use a small threshold (2mm) to prevent tiny rounding discrepancies from spawning an extra blank page
      while (heightLeft > 2) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pageWidthMm, imgHeight);
        heightLeft -= pageHeightMm;
      }

      pdf.save(`${invoice.invoiceNumber || 'invoice'}.pdf`);
      return;
    }

    // Otherwise generate PDF programmatically
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Get data from either structure
    const businessInfo = (invoice as any).from || (invoice as any)._businessInfo || {};
    const clientInfo = invoice.billTo || (invoice as any).to || {};
    const items = invoice.lineItems || (invoice as any).items || [];
    const taxData = (invoice as any).tax || {};
    const discountData = (invoice as any).discount || {};

    const margin = 20;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = margin;

    // Header - Business Info
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text(businessInfo.name || 'Business Name', margin, y);
    y += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    if (businessInfo.address) {
      const addressLines = businessInfo.address.split('\n');
      addressLines.forEach((line: string) => {
        pdf.text(line, margin, y);
        y += 5;
      });
    }
    if (businessInfo.email) {
      pdf.text(businessInfo.email, margin, y);
      y += 5;
    }
    if (businessInfo.phone) {
      pdf.text(businessInfo.phone, margin, y);
      y += 5;
    }

    // Invoice Title and Details (right side)
    pdf.setFontSize(28);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(99, 102, 241); // Primary color
    pdf.text('INVOICE', pageWidth - margin, margin, { align: 'right' });

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    y = margin + 15;
    pdf.text(`Invoice #: ${invoice.invoiceNumber}`, pageWidth - margin, y, { align: 'right' });
    y += 5;
    pdf.text(`Issue Date: ${invoice.issueDate ? format(invoice.issueDate, 'MMM dd, yyyy') : 'N/A'}`, pageWidth - margin, y, { align: 'right' });
    y += 5;
    pdf.text(`Due Date: ${invoice.dueDate ? format(invoice.dueDate, 'MMM dd, yyyy') : 'N/A'}`, pageWidth - margin, y, { align: 'right' });

    y = Math.max(y + 15, 70);

    // Bill To
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Bill To:', margin, y);
    y += 7;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(clientInfo.name || clientInfo.companyName || 'Client Name', margin, y);
    y += 5;

    if (clientInfo.address) {
      const clientAddressLines = clientInfo.address.split('\n');
      clientAddressLines.forEach((line: string) => {
        pdf.text(line, margin, y);
        y += 5;
      });
    }
    if (clientInfo.email) {
      pdf.text(clientInfo.email, margin, y);
      y += 5;
    }
    if (clientInfo.phone) {
      pdf.text(clientInfo.phone, margin, y);
      y += 5;
    }

    y += 10;

    // Line Items Table
    const colWidths = {
      description: 85,
      quantity: 25,
      rate: 30,
      amount: 30,
    };

    // Table Header
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, y, pageWidth - 2 * margin, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text('Description', margin + 2, y + 5);
    pdf.text('Qty', margin + colWidths.description + 2, y + 5);
    pdf.text('Rate', margin + colWidths.description + colWidths.quantity + 2, y + 5);
    pdf.text('Amount', pageWidth - margin - colWidths.amount + 2, y + 5);
    y += 10;

    // Table Rows
    pdf.setFont('helvetica', 'normal');
    items.forEach((item: any) => {
      if (y > pageHeight - 50) {
        pdf.addPage();
        y = margin;
      }

      pdf.text(item.description || '', margin + 2, y);
      pdf.text(String(item.quantity || 0), margin + colWidths.description + 2, y);
      pdf.text((item.unitPrice || item.rate || 0).toFixed(2), margin + colWidths.description + colWidths.quantity + 2, y);
      pdf.text((item.subtotal || item.amount || 0).toFixed(2), pageWidth - margin - colWidths.amount + 2, y);
      y += 7;
    });

    y += 5;

    // Totals
    const totalsX = pageWidth - margin - 60;
    const subtotal = invoice.subtotal || (invoice as any).subtotal || 0;
    const discountAmount = (invoice as any).discountAmount || invoice.totalDiscount || 0;
    const total = (invoice as any).total || invoice.totalAmount || 0;

    pdf.setFont('helvetica', 'normal');
    pdf.text('Subtotal:', totalsX, y);
    pdf.text(`${invoice.currency} ${subtotal.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
    y += 7;

    const headerTaxes = invoice.settings?.headerTax?.taxes || (taxData && taxData.type !== 'none' ? [taxData] : []);
    headerTaxes.forEach((t: any) => {
      if (!t.type || t.type === 'none') return;
      let amt = 0;
      if (t.type === 'percentage' || t.type === 'vat' || t.type === 'sst') {
        amt = (subtotal * (t.value || 0)) / 100;
      } else if (t.type === 'fixed') {
        amt = t.value || 0;
      }
      
      pdf.text(`${t.label || 'Tax'} (${t.type === 'percentage' || t.type === 'vat' || t.type === 'sst' ? t.value + '%' : invoice.currency + ' ' + t.value}):`, totalsX, y);
      pdf.text(`${invoice.currency} ${amt.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
      y += 7;
    });

    if (discountData.type && discountData.type !== 'none') {
      pdf.text(`Discount (${discountData.type === 'percentage' ? discountData.value + '%' : invoice.currency + ' ' + discountData.value}):`, totalsX, y);
      pdf.text(`-${invoice.currency} ${discountAmount.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
      y += 7;
    }

    // Draw line above total
    pdf.setLineWidth(0.5);
    pdf.line(totalsX, y, pageWidth - margin, y);
    y += 7;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('Total:', totalsX, y);
    pdf.text(`${invoice.currency} ${total.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });

    y += 15;

    // Notes and Terms
    if (invoice.notes) {
      if (y > pageHeight - 40) {
        pdf.addPage();
        y = margin;
      }
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Notes:', margin, y);
      y += 5;
      pdf.setFont('helvetica', 'normal');
      const notesLines = pdf.splitTextToSize(invoice.notes, pageWidth - 2 * margin);
      pdf.text(notesLines, margin, y);
      y += notesLines.length * 5 + 5;
    }

    if (invoice.terms) {
      if (y > pageHeight - 40) {
        pdf.addPage();
        y = margin;
      }
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Terms & Conditions:', margin, y);
      y += 5;
      pdf.setFont('helvetica', 'normal');
      const termsLines = pdf.splitTextToSize(invoice.terms, pageWidth - 2 * margin);
      pdf.text(termsLines, margin, y);
    }

    // Save the PDF
    pdf.save(`${invoice.invoiceNumber || 'invoice'}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    MYR: 'RM',
    USD: '$',
    EUR: '€',
    GBP: '£',
    SGD: 'S$',
    AUD: 'A$',
    JPY: '¥',
    CNY: '¥',
  };
  return symbols[currency] || currency;
}

export async function exportQuotationToPDF(quotation: Quotation, elementId?: string): Promise<void> {
  try {
    if (elementId) {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error('Quotation element not found');
      }

      console.log('Capturing quotation with template design...');
      const canvas = await captureElement(element);
      console.log(`Canvas created: ${canvas.width}x${canvas.height}`);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidthMm  = 210; // A4 width in mm
      const pageHeightMm = 297; // A4 height in mm
      const imgHeight    = (canvas.height * pageWidthMm) / canvas.width;
      let heightLeft     = imgHeight;
      let position       = 0;

      // First page
      pdf.addImage(imgData, 'PNG', 0, position, pageWidthMm, imgHeight);
      heightLeft -= pageHeightMm;

      while (heightLeft > 2) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pageWidthMm, imgHeight);
        heightLeft -= pageHeightMm;
      }

      pdf.save(`${quotation.quotationNumber || 'quotation'}.pdf`);
      return;
    }

    // Default programmatic generation fallback (seldom used as we always pass elementId)
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    pdf.setFontSize(24);
    const isEstimate = quotation.quotationNumber?.startsWith('EST');
    pdf.text(isEstimate ? 'ESTIMATE' : 'QUOTATION', 20, 20);
    pdf.save(`${quotation.quotationNumber || 'quotation'}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
}
